import { BATTLESHIP_SHIPS, type BattleCell, type BattleshipView } from '@minigame-race/contracts';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { color } from '../theme.js';
import { Icon } from '../components/Icon.js';

/**
 * Battleship.
 *
 *  PLACING — you arrange your fleet on your own grid (drag ships; rotate with the
 *  ⟳ handle) against the SERVER's 15s clock. You must DEPLOY within that window —
 *  the server forfeits you (a loss) if you don't. Once you deploy, a waiting
 *  overlay shows the rival's remaining deploy time. When both have deployed the
 *  server flips the phase to firing and the board switches itself.
 *
 *  FIRING — you only ever see the ENEMY grid (big, centred, fits the slot). When
 *  the rival fires at you the board ring pulses so you notice the turn passing.
 */

interface PlacedShip { row: number; col: number; length: number; horizontal: boolean }

export function BattleshipBoard({
  view,
  yourTurn,
  onMove,
}: {
  view: BattleshipView;
  yourTurn: boolean;
  onMove: (move: unknown) => void;
}) {
  const n = view.size;

  if (view.phase === 'placing') {
    if (view.youPlaced) return <DeployWaiting msLeft={view.opponentMsLeft} capMs={view.opponentCapMs} />;
    return (
      <Placement
        n={n}
        msLeft={view.moveMsLeft}
        opponentPlaced={view.opponentPlaced}
        onDeploy={(ships) => onMove({ type: 'place', ships })}
      />
    );
  }

  return <Firing view={view} yourTurn={yourTurn} onFire={(cell) => onMove({ type: 'fire', cell })} />;
}

/* ------------------------------------------------------------------ */
/* Deploy waiting overlay                                              */
/* ------------------------------------------------------------------ */

function DeployWaiting({ msLeft, capMs }: { msLeft: number; capMs: number }) {
  const secs = useLocalCountdown(true, msLeft, 'deploy-wait');
  const frac = capMs > 0 ? Math.max(0, Math.min(1, (secs * 1000) / capMs)) : 0;
  return (
    <div style={{ width: 'min(88cqw, 88cqh, 340px)', margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
      <Icon name="anchor" size={40} stroke={1.6} style={{ color: color.lime }} />
      <div style={{ fontSize: 15, fontWeight: 700, color: color.lime, letterSpacing: '.04em' }}>FLEET DEPLOYED</div>
      <div style={{ fontSize: 13, color: 'rgba(234,255,234,.6)' }}>Waiting for your rival to deploy…</div>
      <div style={{ width: '100%', maxWidth: 240 }}>
        <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${frac * 100}%`, borderRadius: 999, background: secs <= 2 ? color.redBright : color.cyan, transition: 'width .2s linear' }} />
        </div>
        <div style={{ marginTop: 8, fontFamily: "'JetBrains Mono',monospace", fontSize: 22, fontWeight: 800, color: secs <= 2 ? color.redBright : 'rgba(234,255,234,.85)' }}>
          0:{String(secs).padStart(2, '0')}
        </div>
        <div style={{ fontSize: 11, color: 'rgba(234,255,234,.45)', marginTop: 2 }}>rival deploys or forfeits</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Firing — enemy board only                                           */
/* ------------------------------------------------------------------ */

const CELL_BG: Record<BattleCell, string> = {
  empty: 'rgba(10,40,70,.55)',
  ship: 'rgba(10,40,70,.55)', // never shown on the enemy board
  miss: 'rgba(255,255,255,.10)',
  hit: 'rgba(255,176,32,.85)',
  sunk: 'rgba(255,46,77,.9)',
};
const CELL_GLYPH: Partial<Record<BattleCell, string>> = { miss: '·', hit: '✳', sunk: '✕' };

function Firing({
  view,
  yourTurn,
  onFire,
}: {
  view: BattleshipView;
  yourTurn: boolean;
  onFire: (cell: number) => void;
}) {
  const n = view.size;

  // Incoming shot: our own grid gained a shot since last view → pulse the ring.
  const shotCount = (g: readonly BattleCell[]): number => g.filter((c) => c === 'miss' || c === 'hit' || c === 'sunk').length;
  const prevShots = useRef<number>(shotCount(view.yourGrid));
  const [incoming, setIncoming] = useState(0);
  useEffect(() => {
    const now = shotCount(view.yourGrid);
    if (now > prevShots.current) setIncoming((k) => k + 1);
    prevShots.current = now;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.yourGrid]);

  const secs = useLocalCountdown(yourTurn, view.moveMsLeft, 'fire');
  const remaining = view.theirGrid.filter((c) => c === 'empty').length;

  return (
    <div style={{ width: 'min(92cqw, 78cqh, 440px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'min(1.6cqh,12px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, letterSpacing: '.18em', color: yourTurn ? color.lime : color.cyan, fontWeight: 700 }}>
          {yourTurn ? 'FIRE AT THE ENEMY' : 'RIVAL IS FIRING…'}
        </span>
        {yourTurn && (
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 14, fontWeight: 800, color: secs <= 1 ? color.redBright : 'rgba(234,255,234,.8)' }}>
            0:{String(secs).padStart(2, '0')}
          </span>
        )}
      </div>

      <div
        key={incoming}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${n},1fr)`,
          gap: 'min(1.2cqw,5px)',
          padding: 'min(1.6cqw,8px)',
          borderRadius: 14,
          background: 'rgba(0,0,0,.4)',
          border: `1px solid ${yourTurn ? 'rgba(166,255,46,.3)' : 'rgba(34,224,255,.25)'}`,
          animation: incoming ? 'bsIncoming .9s ease-out' : undefined,
        }}
      >
        {view.theirGrid.map((c, i) => {
          const known = c !== 'empty';
          const canFire = yourTurn && !known;
          return (
            <button
              key={i}
              type="button"
              disabled={!canFire}
              onClick={() => canFire && onFire(i)}
              aria-label={`Fire at ${i}`}
              style={{
                aspectRatio: '1 / 1',
                width: '100%',
                padding: 0,
                borderRadius: 5,
                border: canFire ? '1px solid rgba(166,255,46,.25)' : '1px solid transparent',
                background: CELL_BG[c],
                color: c === 'miss' ? 'rgba(255,255,255,.6)' : 'rgba(0,0,0,.85)',
                fontSize: 'min(4.5cqw,20px)',
                fontWeight: 800,
                cursor: canFire ? 'crosshair' : 'default',
                display: 'grid',
                placeItems: 'center',
                touchAction: 'manipulation',
                animation: known ? 'bsSplash .3s ease-out' : undefined,
              }}
            >
              {CELL_GLYPH[c] ?? ''}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(234,255,234,.5)', padding: '0 4px' }}>
        <span>✳ hit&nbsp; ✕ sunk&nbsp; · miss</span>
        <span>{remaining} unknown</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Placement — drag ships onto your own grid                           */
/* ------------------------------------------------------------------ */

function Placement({
  n,
  msLeft,
  opponentPlaced,
  onDeploy,
}: {
  n: number;
  msLeft: number;
  opponentPlaced: boolean;
  onDeploy: (ships: PlacedShip[]) => void;
}) {
  const [ships, setShips] = useState<(PlacedShip | null)[]>(() => BATTLESHIP_SHIPS.map(() => null));
  const [active, setActive] = useState<number | null>(0);
  const [preview, setPreview] = useState<{ row: number; col: number } | null>(null);
  const [pendingHoriz, setPendingHoriz] = useState(true);
  const gridRef = useRef<HTMLDivElement>(null);

  const done = ships.every((s) => s !== null);

  const deploy = (): void => {
    if (!done) return;
    onDeploy(ships.filter((s): s is PlacedShip => s !== null));
  };

  const secs = useLocalCountdown(true, msLeft, 'place'); // server owns the real clock
  const urgent = secs <= 4;

  /* occupancy excluding the active ship (so it can move over its own spot) */
  const occupiedBy = (excludeIdx: number | null): Set<number> => {
    const occ = new Set<number>();
    ships.forEach((s, idx) => { if (s && idx !== excludeIdx) for (const c of shipCells(s, n)) occ.add(c); });
    return occ;
  };
  const fits = (row: number, col: number, length: number, horizontal: boolean, excludeIdx: number | null): number[] | null => {
    const occ = occupiedBy(excludeIdx);
    const cells: number[] = [];
    for (let k = 0; k < length; k++) {
      const r = horizontal ? row : row + k;
      const c = horizontal ? col + k : col;
      if (r < 0 || c < 0 || r >= n || c >= n) return null;
      const idx = r * n + c;
      if (occ.has(idx)) return null;
      cells.push(idx);
    }
    return cells;
  };
  const clampOrigin = (row: number, col: number, length: number, horizontal: boolean): { row: number; col: number } => ({
    row: horizontal ? Math.min(row, n - 1) : Math.min(row, n - length),
    col: horizontal ? Math.min(col, n - length) : Math.min(col, n - 1),
  });
  const cellFromPointer = (clientX: number, clientY: number): { row: number; col: number } | null => {
    const el = gridRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const col = Math.max(0, Math.min(n - 1, Math.floor(((clientX - rect.left) / rect.width) * n)));
    const row = Math.max(0, Math.min(n - 1, Math.floor(((clientY - rect.top) / rect.height) * n)));
    return { row, col };
  };

  const activeShip = active === null ? null : ships[active] ?? null;
  const activeLen = active === null ? 0 : BATTLESHIP_SHIPS[active]!;
  const effHoriz = activeShip?.horizontal ?? pendingHoriz;

  const moveActiveTo = (row: number, col: number): void => {
    if (active === null) return;
    setPreview(clampOrigin(row, col, activeLen, effHoriz));
  };
  const commitPreview = (): void => {
    if (active === null || !preview) return;
    if (!fits(preview.row, preview.col, activeLen, effHoriz, active)) return;
    setShips((prev) => {
      const next = [...prev];
      next[active] = { row: preview.row, col: preview.col, length: activeLen, horizontal: effHoriz };
      return next;
    });
    // auto-advance to the next unplaced ship
    const nextIdx = ships.findIndex((s, i) => s === null && i !== active);
    if (nextIdx >= 0) { setActive(nextIdx); setPendingHoriz(true); }
  };
  const rotateActive = (): void => {
    if (active === null) return;
    const horiz = !effHoriz;
    const base = preview ?? activeShip ?? { row: 0, col: 0 };
    const o = clampOrigin(base.row, base.col, activeLen, horiz);
    setPendingHoriz(horiz);
    setShips((prev) => {
      const next = [...prev];
      if (next[active]) next[active] = { row: o.row, col: o.col, length: activeLen, horizontal: horiz };
      return next;
    });
    setPreview(o);
  };

  const previewCells = (() => {
    if (active === null || !preview) return { cells: new Set<number>(), valid: false };
    const c = fits(preview.row, preview.col, activeLen, effHoriz, active);
    return { cells: new Set(c ?? []), valid: c !== null };
  })();

  const placedSet = new Map<number, number>();
  ships.forEach((s, idx) => { if (s) for (const c of shipCells(s, n)) placedSet.set(c, idx); });

  const onPointerDown = (e: React.PointerEvent): void => {
    const cell = cellFromPointer(e.clientX, e.clientY);
    if (!cell) return;
    const idx = placedSet.get(cell.row * n + cell.col);
    if (idx !== undefined) { setActive(idx); setPendingHoriz(ships[idx]!.horizontal); }
    (e.target as Element).setPointerCapture?.(e.pointerId);
    moveActiveTo(cell.row, cell.col);
  };
  const onPointerMove = (e: React.PointerEvent): void => {
    if (preview === null) return;
    const cell = cellFromPointer(e.clientX, e.clientY);
    if (cell) moveActiveTo(cell.row, cell.col);
  };
  const onPointerUp = (): void => { commitPreview(); setPreview(null); };

  return (
    <div style={{ width: 'min(90cqw, 66cqh, 380px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'min(1.4cqh,10px)' }}>
      {/* header + server-driven timer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, letterSpacing: '.1em', color: color.lime, fontWeight: 700 }}>PLACE &amp; DEPLOY</span>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 15, fontWeight: 800, color: urgent ? color.redBright : 'rgba(234,255,234,.75)', animation: urgent ? 'rhBlinkRed 1s steps(2,end) infinite' : undefined }}>
          0:{String(secs).padStart(2, '0')}
        </span>
      </div>

      {/* ship tray */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {BATTLESHIP_SHIPS.map((len, idx) => {
          const isPlaced = ships[idx] !== null;
          const isActive = active === idx;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => { setActive(idx); setPendingHoriz(ships[idx]?.horizontal ?? true); setPreview(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 3, padding: '6px 10px', borderRadius: 8,
                border: `2px solid ${isActive ? color.lime : isPlaced ? 'rgba(166,255,46,.25)' : 'rgba(234,255,234,.22)'}`,
                background: isActive ? 'rgba(166,255,46,.14)' : 'rgba(255,255,255,.03)',
                cursor: 'pointer', opacity: isPlaced && !isActive ? 0.5 : 1, touchAction: 'manipulation',
              }}
            >
              {Array.from({ length: len }, (_, k) => (
                <span key={k} style={{ width: 11, height: 11, borderRadius: 2, background: isActive ? color.lime : 'rgba(166,255,46,.5)', outline: '1px solid rgba(0,0,0,.3)' }} />
              ))}
              {isPlaced && <span style={{ fontSize: 11, color: color.lime, marginLeft: 2 }}>✓</span>}
            </button>
          );
        })}
      </div>

      {/* the board */}
      <div
        ref={gridRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position: 'relative', display: 'grid', gridTemplateColumns: `repeat(${n},1fr)`,
          gap: 'min(1.2cqw,5px)', padding: 'min(1.6cqw,8px)', borderRadius: 14,
          background: 'rgba(0,0,0,.4)', border: '1px solid rgba(166,255,46,.16)',
          touchAction: 'none', userSelect: 'none',
        }}
      >
        {Array.from({ length: n * n }, (_, i) => {
          const shipIdx = placedSet.get(i);
          const isShip = shipIdx !== undefined;
          const isActiveShip = isShip && shipIdx === active;
          const inPreview = previewCells.cells.has(i);
          const previewBad = inPreview && !previewCells.valid;
          return (
            <div
              key={i}
              style={{
                aspectRatio: '1 / 1',
                borderRadius: 4,
                background: isShip
                  ? isActiveShip ? 'rgba(166,255,46,.85)' : 'rgba(166,255,46,.6)'
                  : inPreview ? (previewBad ? 'rgba(255,46,77,.4)' : 'rgba(166,255,46,.28)') : 'rgba(10,40,70,.55)',
                outline: isShip ? '2px solid rgba(255,255,255,.55)' : inPreview && !previewBad ? '2px dashed rgba(166,255,46,.7)' : 'none',
                outlineOffset: -2,
                transition: 'background .08s ease',
              }}
            />
          );
        })}
        <RotateHandle active={active} preview={preview} activeShip={activeShip} n={n} effHoriz={effHoriz} activeLen={activeLen} onRotate={rotateActive} gridRef={gridRef} />
      </div>

      {/* actions */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={rotateActive} disabled={active === null} style={{ ...ctrlBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="rotate" size={15} /> ROTATE
        </button>
        <button
          type="button"
          onClick={deploy}
          disabled={!done}
          style={{ ...ctrlBtn, borderColor: done ? 'rgba(166,255,46,.6)' : 'rgba(234,255,234,.15)', background: done ? 'rgba(166,255,46,.16)' : 'rgba(255,255,255,.03)', color: done ? color.lime : 'rgba(234,255,234,.35)', fontWeight: 800, cursor: done ? 'pointer' : 'default' }}
        >
          DEPLOY FLEET
        </button>
      </div>
      <div style={{ textAlign: 'center', fontSize: 11, color: 'rgba(234,255,234,.5)' }}>
        {done ? 'Tap DEPLOY before the clock runs out — or you forfeit.' : 'Drag each ship onto the grid, then deploy.'}
        {opponentPlaced && <span style={{ color: color.amber }}> · rival has deployed</span>}
      </div>
    </div>
  );
}

/** A ⟳ button floating at the centre of the active ship (placed or previewing). */
function RotateHandle({
  active, preview, activeShip, n, effHoriz, activeLen, onRotate, gridRef,
}: {
  active: number | null;
  preview: { row: number; col: number } | null;
  activeShip: PlacedShip | null;
  n: number;
  effHoriz: boolean;
  activeLen: number;
  onRotate: () => void;
  gridRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);
  const origin = active === null ? null : preview ?? activeShip;

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el || active === null || !origin) { setBox(null); return; }
    const rect = el.getBoundingClientRect();
    const cw = rect.width / n;
    const midK = (activeLen - 1) / 2;
    setBox({
      left: (origin.col + (effHoriz ? midK : 0) + 0.5) * cw,
      top: (origin.row + (effHoriz ? 0 : midK) + 0.5) * cw,
    });
  }, [active, origin?.row, origin?.col, effHoriz, activeLen, n, gridRef]);

  if (!box || active === null) return null;
  return (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onRotate(); }}
      aria-label="Rotate ship"
      style={{
        position: 'absolute', left: box.left, top: box.top, transform: 'translate(-50%,-50%)',
        width: 26, height: 26, borderRadius: '50%', border: '2px solid rgba(0,0,0,.5)',
        background: 'rgba(255,255,255,.92)', color: '#0a2a10',
        display: 'grid', placeItems: 'center', cursor: 'pointer', zIndex: 3, touchAction: 'manipulation',
        boxShadow: '0 1px 4px rgba(0,0,0,.4)',
      }}
    >
      <Icon name="rotate" size={15} stroke={2.4} />
    </button>
  );
}

const ctrlBtn: React.CSSProperties = {
  padding: '10px 18px', borderRadius: 999, border: '1px solid rgba(166,255,46,.3)',
  background: 'rgba(166,255,46,.06)', color: 'rgba(234,255,234,.85)', fontSize: 13, fontWeight: 700,
  letterSpacing: '.04em', cursor: 'pointer', touchAction: 'manipulation',
};

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

/**
 * Smoothly ticks down from a server-provided `msLeft` without tearing the
 * interval on every re-push. The server owns the real deadline (and forfeits on
 * expiry); this is the visible countdown.
 */
function useLocalCountdown(active: boolean, serverMsLeft: number, sig: string): number {
  const [secs, setSecs] = useState(active ? Math.ceil(serverMsLeft / 1000) : 0);
  const deadline = useRef(0);
  useEffect(() => { deadline.current = Date.now() + serverMsLeft; }, [serverMsLeft]);
  useEffect(() => {
    if (!active) { setSecs(0); return; }
    deadline.current = Date.now() + serverMsLeft;
    setSecs(Math.ceil(serverMsLeft / 1000));
    const id = window.setInterval(() => setSecs(Math.ceil(Math.max(0, deadline.current - Date.now()) / 1000)), 200);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, sig]);
  return secs;
}

function shipCells(s: PlacedShip, n: number): number[] {
  const out: number[] = [];
  for (let k = 0; k < s.length; k++) {
    const r = s.horizontal ? s.row : s.row + k;
    const c = s.horizontal ? s.col + k : s.col;
    out.push(r * n + c);
  }
  return out;
}
